/**
 * 1stbuddha — wishlist.js
 *
 * Connects the wishlist hearts ([data-wishlist-toggle], snippets/product-card)
 * to Swym Wishlist Plus.
 *
 * Performance
 *   - Loaded with `defer`; Swym's own script is injected by its app embed
 *     (async). Nothing here runs until Swym calls back via window.SwymCallbacks,
 *     and the first list fetch waits for an idle moment.
 *   - Hearts stay hidden until then (html.has-wishlist), so a slow or disabled
 *     Swym never blocks rendering or shifts the layout.
 *
 * Exposes window.themeWishlist:
 *   sync(root)          set aria-pressed on the hearts inside root
 *   add(piece)          save { productId, variantId, url }
 *   remove(piece)       remove { productId } (variant and url are looked up)
 *   items()             saved pieces [{ productId, variantId, url, addedAt }], newest first
 *   ready               promise, resolves after Swym has loaded and the list
 *                       was fetched once (never rejects; pages add a timeout)
 *   isReady()           Swym has loaded
 *   canShare()          Swym offers shareable list links
 *   shareUrl()          promise of a shareable link to the list
 * Every change dispatches `wishlist:change` on document ({ detail: { count } }).
 * Swym API used: fetchLists, createList, addToList, deleteFromList,
 * generateSharedListURL, evtLayer events addedToWishlist / removedFromWishlist.
 */
(() => {
  if (window.themeWishlist) return;

  const SELECTOR = '[data-wishlist-toggle]';
  const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 200));
  const config = (() => {
    try {
      return JSON.parse(document.getElementById('ThemeConfig')?.textContent || '{}');
    } catch {
      return {};
    }
  })();
  const strings = config.strings || {};

  const state = { swat: null, listId: null, saved: new Set(), items: [] };
  let markReady;
  const ready = new Promise((resolve) => {
    markReady = resolve;
  });

  const toast = (detail) => document.dispatchEvent(new CustomEvent('toast:show', { detail }));

  const sync = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach((button) => {
      button.setAttribute('aria-pressed', String(state.saved.has(button.dataset.productId)));
    });
  };

  const changed = () => {
    sync();
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { count: state.items.length } }));
  };

  const setItems = (contents) => {
    state.items = contents
      .map((item) => ({
        productId: String(item.empi),
        variantId: String(item.epi),
        url: item.du || '',
        addedAt: Number(item.cts || item.dt || 0),
      }))
      .sort((a, b) => b.addedAt - a.addedAt);
    state.saved = new Set(state.items.map((item) => item.productId));
  };

  const fetchLists = () =>
    new Promise((resolve) => {
      state.swat.fetchLists({
        callbackFn: (lists) => {
          const list = Array.isArray(lists) ? lists[0] : null;
          state.listId = list?.lid || null;
          setItems(Array.isArray(list?.listcontents) ? list.listcontents : []);
          changed();
          resolve();
        },
        errorFn: () => resolve(),
      });
    });

  const ensureList = () =>
    new Promise((resolve, reject) => {
      if (state.listId) {
        resolve(state.listId);
        return;
      }
      state.swat.createList(
        { lname: strings.wishlistName || 'My Wishlist' },
        ({ lid }) => {
          state.listId = lid;
          resolve(lid);
        },
        reject
      );
    });

  const remember = (piece) => {
    if (state.saved.has(piece.productId)) return;
    state.items.unshift({ ...piece, addedAt: Date.now() });
    state.saved.add(piece.productId);
  };

  const forget = (productId) => {
    state.items = state.items.filter((item) => item.productId !== productId);
    state.saved.delete(productId);
  };

  const toggle = async (button) => {
    const productId = button.dataset.productId;
    const adding = !state.saved.has(productId);
    const product = {
      epi: Number(button.dataset.variantId),
      empi: Number(productId),
      du: button.dataset.productUrl,
    };

    button.setAttribute('aria-busy', 'true');
    try {
      const listId = await ensureList();
      await new Promise((resolve, reject) => {
        const method = adding ? state.swat.addToList : state.swat.deleteFromList;
        method.call(state.swat, listId, product, resolve, reject);
      });

      if (adding) {
        remember({ productId, variantId: String(product.epi), url: product.du });
        button.classList.remove('is-popping');
        void button.offsetWidth;
        button.classList.add('is-popping');
      } else {
        forget(productId);
      }
      // No success toast: Swym's Storefront Layout embed shows its own
      // "Item Saved" / "Item Removed" notification. Errors still toast below.
      changed();
    } catch {
      toast({ message: strings.wishlistError, variant: 'error' });
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  const onSwymReady = (swat) => {
    state.swat = swat;
    idle(() => {
      document.documentElement.classList.add('has-wishlist');
      fetchLists().then(() => markReady(true));

      const { evtLayer, JSEvents } = swat;
      if (evtLayer && JSEvents) {
        evtLayer.addEventListener(JSEvents.addedToWishlist, fetchLists);
        evtLayer.addEventListener(JSEvents.removedFromWishlist, fetchLists);
      }
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest(SELECTOR);
    if (!button || !state.swat || button.hasAttribute('aria-busy')) return;
    event.preventDefault();
    toggle(button);
  });

  // Hearts rendered later (theme editor, section rendering, load more)
  document.addEventListener('shopify:section:load', (event) => sync(event.target));

  // Used by the cart's "Move to wishlist": resolves once the piece is saved.
  const add = async ({ productId, variantId, url }) => {
    if (!state.swat) throw new Error('Wishlist not ready');
    const id = String(productId);
    if (state.saved.has(id)) return;
    const listId = await ensureList();
    await new Promise((resolve, reject) => {
      state.swat.addToList.call(state.swat, listId, { epi: Number(variantId), empi: Number(productId), du: url }, resolve, reject);
    });
    remember({ productId: id, variantId: String(variantId), url });
    changed();
  };

  // Used by the wishlist page's × button.
  const remove = async ({ productId }) => {
    if (!state.swat || !state.listId) throw new Error('Wishlist not ready');
    const id = String(productId);
    const saved = state.items.find((item) => item.productId === id);
    if (!saved) return;
    await new Promise((resolve, reject) => {
      state.swat.deleteFromList.call(
        state.swat,
        state.listId,
        { epi: Number(saved.variantId), empi: Number(id), du: saved.url },
        resolve,
        reject
      );
    });
    forget(id);
    changed();
  };

  const canShare = () => Boolean(state.swat && typeof state.swat.generateSharedListURL === 'function');

  const shareUrl = async () => {
    if (!canShare()) throw new Error('Sharing unavailable');
    const listId = await ensureList();
    return new Promise((resolve, reject) => {
      state.swat.generateSharedListURL(
        listId,
        (link) => (link ? resolve(String(link)) : reject(new Error('No link'))),
        reject
      );
    });
  };

  window.themeWishlist = {
    sync,
    add,
    remove,
    ready,
    canShare,
    shareUrl,
    items: () => state.items.map((item) => ({ ...item })),
    isReady: () => Boolean(state.swat),
  };
  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(onSwymReady);
})();
